'use client';
import React, { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { CheckCircle, Star, MapPin, Users, Calendar, Shield, Smartphone, ShoppingBag, Briefcase, Globe, Heart, Gift, ChevronDown, ChevronUp, MessageCircle, Phone, Mail, Zap, TrendingUp, Award, Package, Wrench, Camera, Code, Palette, BookOpen, Scale, Calculator, Stethoscope, Hammer, Sparkles, Car, ArrowRight } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import QRCodeSection from './QRCodeSection';
import MarketingPlanSection from './MarketingPlanSection';

interface MemberProfile {
  id: string;
  username: string;
  full_name: string;
  first_name: string;
  last_name?: string;
  avatar_url?: string;
  location?: string;
  bio?: string;
  role: string;
  profession?: string;
  created_at: string;
}

interface MemberPage {
  cover_photo_url: string;
  profile_photo_url: string;
  headline: string;
  subheadline: string;
  personal_intro: string;
  about_me: string;
  biography: string;
  whatsapp: string;
  messenger: string;
  call_number: string;
  email_contact: string;
  booking_link: string;
  facebook_url: string;
  instagram_url: string;
  tiktok_url: string;
  youtube_url: string;
  linkedin_url: string;
  twitter_url: string;
  primary_cta_text: string;
  secondary_cta_text: string;
  featured_services: ServiceItem[];
  featured_products: ProductItem[];
  testimonials: TestimonialItem[];
  videos: VideoItem[];
  skills: string[];
  accent_color: string;
  total_visits: number;
  total_qr_scans: number;
  total_registrations: number;
}

interface ServiceItem {
  title: string;
  description: string;
  icon?: string;
}

interface ProductItem {
  title: string;
  price: string;
  image?: string;
  description?: string;
}

interface TestimonialItem {
  name: string;
  role: string;
  text: string;
  avatar?: string;
  rating: number;
}

interface VideoItem {
  title: string;
  url: string;
  thumbnail?: string;
}

interface SponsorInfo {
  username: string;
  full_name: string;
  avatar_url?: string;
  rank?: string;
  joined_year?: string;
}

interface Props {
  username: string;
  isQrScan?: boolean;
  refSource?: string;
}

const navItems = [
  { id: 'welcome', label: 'About' },
  { id: 'features', label: 'Marketplace' },
  { id: 'profile', label: 'Community' },
  { id: 'rewards', label: 'Rewards' },
  { id: 'faq', label: 'FAQ' },
  { id: 'marketing-plan', label: 'Complan' },
];

const defaultPage: MemberPage = {
  cover_photo_url: '',
  profile_photo_url: '',
  headline: 'Build Your Business. Grow Your Network. Earn Through Opportunity.',
  subheadline: 'Join me on RaketNow and get access to the complete digital business ecosystem.',
  personal_intro: '',
  about_me: '',
  biography: '',
  whatsapp: '',
  messenger: '',
  call_number: '',
  email_contact: '',
  booking_link: '',
  facebook_url: '',
  instagram_url: '',
  tiktok_url: '',
  youtube_url: '',
  linkedin_url: '',
  twitter_url: '',
  primary_cta_text: 'Create Your FREE Account',
  secondary_cta_text: 'Download Mobile App',
  featured_services: [],
  featured_products: [],
  testimonials: [],
  videos: [],
  skills: [],
  accent_color: '#FF6B35',
  total_visits: 0,
  total_qr_scans: 0,
  total_registrations: 0,
};

const defaultTestimonials: TestimonialItem[] = [
  { name: 'Maria Santos', role: 'Merchant Partner', text: 'RaketNow helped me grow my online store from zero to thousands of customers in just 3 months. The marketplace is incredible!', rating: 5 },
  { name: 'Juan dela Cruz', role: 'Service Provider', text: 'I now earn ₱50,000+ monthly just from bookings through the platform. The best decision I ever made for my business.', rating: 5 },
  { name: 'Ana Reyes', role: 'Freelancer', text: 'My portfolio on RaketNow gets me 10x more clients than any other platform. The professional profile feature is a game-changer.', rating: 5 },
  { name: 'Pedro Lim', role: 'Entrepreneur', text: 'The referral rewards system is transparent and fair. I love that it rewards real marketplace activity, not just recruitment.', rating: 5 },
  { name: 'Rosa Garcia', role: 'Customer', text: 'Finding trusted service providers used to be a nightmare. RaketNow made it so easy and safe. I use it every week!', rating: 5 },
  { name: 'Carlo Mendoza', role: 'Community Builder', text: 'The community features helped me build a network of 200+ professionals. The connections I made here are priceless.', rating: 5 },
];

const faqItems = [
  { q: 'Is registration free?', a: 'Yes! Creating your RaketNow account is completely free. No hidden fees, no credit card required.' },
  { q: 'Can I sell products for free?', a: 'Yes! Listing products on the marketplace is free. The platform only earns a small fee on successful transactions.' },
  { q: 'Can I become a service provider for free?', a: 'Yes! Creating a provider account and listing your services is completely free.' },
  { q: 'Can I build my online portfolio?', a: 'Yes! Every member gets a professional profile page to showcase their skills, experience, and portfolio.' },
  { q: 'Can I network with other professionals?', a: 'Yes! The community features let you connect, follow, and collaborate with thousands of professionals.' },
  { q: 'Do I need to activate the referral feature?', a: 'No. All marketplace features are available without activating the optional network marketing feature. Activation is only for members who want to build a referral organization.' },
  { q: 'How does the platform earn revenue?', a: 'RaketNow primarily earns through marketplace transaction fees and value-added services. There are no monthly membership fees for basic access.' },
];

const serviceCategories = [
  { icon: Hammer, label: 'Home Repair' }, { icon: Sparkles, label: 'Cleaning' },
  { icon: Zap, label: 'Electrical' }, { icon: Wrench, label: 'Plumbing' },
  { icon: Palette, label: 'Design' }, { icon: Code, label: 'Programming' },
  { icon: TrendingUp, label: 'Marketing' }, { icon: Camera, label: 'Photography' },
  { icon: BookOpen, label: 'Tutoring' }, { icon: Scale, label: 'Legal' },
  { icon: Calculator, label: 'Accounting' }, { icon: Stethoscope, label: 'Healthcare' },
  { icon: Hammer, label: 'Construction' }, { icon: Sparkles, label: 'Beauty' },
  { icon: Car, label: 'Automotive' }, { icon: Package, label: 'Delivery' },
];

const productCategories = [
  'Electronics', 'Fashion', 'Beauty', 'Home & Living', 'Food & Beverage',
  'Office Supplies', 'Digital Products', 'Handmade Goods',
];

const rewardFlow = [
  { step: 'Customer buys a product or books a service', icon: ShoppingBag },
  { step: 'Platform collects a small transaction fee', icon: Calculator },
  { step: 'A portion funds platform operations', icon: Globe },
  { step: 'A portion is allocated to the community rewards pool', icon: Gift },
  { step: 'Eligible members receive reward points based on referral network activity', icon: Award },
  { step: 'Points convert to cash, vouchers, discounts, or other incentives', icon: TrendingUp },
];

function buildFallbackProfile(username: string): MemberProfile {
  return {
    id: '',
    username,
    full_name: username.charAt(0).toUpperCase() + username.slice(1).toLowerCase(),
    first_name: username.charAt(0).toUpperCase() + username.slice(1).toLowerCase(),
    avatar_url: undefined,
    location: 'Philippines',
    bio: '',
    profession: '',
    role: 'member',
    created_at: new Date().toISOString(),
  };
}

export default function MemberLandingPage({ username, isQrScan, refSource }: Props) {
  const [profile, setProfile] = useState<MemberProfile>(buildFallbackProfile(username));
  const [page, setPage] = useState<MemberPage>(defaultPage);
  const [sponsor, setSponsor] = useState<SponsorInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://raketnow.org';
  const pageUrl = `${siteUrl}/r/${username}`;

  // Canvas wave animation
  useEffect(() => {
    // Only start canvas animation after loading is complete (canvas is in the DOM)
    if (loading) return;

    const canvas = canvasRef.current;

    if (!canvas) {
      console.error('[Canvas] canvasRef.current is null — canvas element not mounted yet');
      return;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('[Canvas] getContext("2d") returned null');
      return;
    }

    let animId: number;
    let running = true;

    // Wave definitions — filled bands
    const filledWaves = [
      { yFrac: 0.90, amp: 90, freq: 1.6, speed: 0.007, phase: 0,   colorR: 249, colorG: 115, colorB: 22,  aMax: 0.22 }, // orange
      { yFrac: 1.00, amp: 75, freq: 2.0, speed: 0.010, phase: 1.2, colorR: 250, colorG: 204, colorB: 21,  aMax: 0.20 }, // yellow
      { yFrac: 1.10, amp: 65, freq: 1.4, speed: 0.006, phase: 2.4, colorR: 15,  colorG: 23,  colorB: 42,  aMax: 0.18 }, // navy
      { yFrac: 0.80, amp: 95, freq: 2.4, speed: 0.012, phase: 0.7, colorR: 249, colorG: 115, colorB: 22,  aMax: 0.16 }, // orange
      { yFrac: 0.70, amp: 80, freq: 1.8, speed: 0.008, phase: 3.1, colorR: 250, colorG: 204, colorB: 21,  aMax: 0.22 }, // yellow
    ];

    // Stroke-only lines
    const strokeWaves = [
      { yFrac: 0.77, amp: 120, freq: 0.8, speed: 0.005, phase: 0.5, strokeR: 249, strokeG: 115, strokeB: 22  }, // orange
      { yFrac: 0.63, amp: 95,  freq: 0.9, speed: 0.004, phase: 1.8, strokeR: 250, strokeG: 204, strokeB: 21  }, // yellow
      { yFrac: 0.53, amp: 70,  freq: 0.7, speed: 0.003, phase: 3.5, strokeR: 250, strokeG: 204, strokeB: 21  }, // yellow
    ];

    function resize() {
      if (!canvas) return;
      canvas.width  = window.innerWidth;
      canvas.height = Math.max(document.documentElement.scrollHeight, window.innerHeight);
    }

    function draw() {
      if (!canvas || !ctx || !running) return;
      const W = canvas.width;
      const H = canvas.height;
      const freqScale = W / 1440;

      // Layer 0: Fallback gradient background
      const fallbackGrad = ctx.createLinearGradient(0, 0, 0, H);
      fallbackGrad.addColorStop(0, '#DDE6F0');
      fallbackGrad.addColorStop(0.5, '#EEF2F7');
      fallbackGrad.addColorStop(1, '#E2EAF4');
      ctx.fillStyle = fallbackGrad;
      ctx.fillRect(0, 0, W, H);

      // Layer 1: Base solid fill
      ctx.fillStyle = '#EEF2F7';
      ctx.fillRect(0, 0, W, H);

      // Layer 2: Filled wave bands
      for (const w of filledWaves) {
        w.phase += w.speed;
        const cy = w.yFrac * H;

        ctx.beginPath();
        ctx.moveTo(0, cy);
        for (let x = 0; x <= W; x += 1) {
          const y = cy + Math.sin((x * w.freq * freqScale * Math.PI * 2) / W + w.phase) * w.amp;
          ctx.lineTo(x, y);
        }
        ctx.lineTo(W, H);
        ctx.lineTo(0, H);
        ctx.closePath();

        const grad = ctx.createLinearGradient(0, cy - w.amp, 0, H);
        grad.addColorStop(0, `rgba(${w.colorR},${w.colorG},${w.colorB},${w.aMax})`);
        grad.addColorStop(0.6, `rgba(${w.colorR},${w.colorG},${w.colorB},${(w.aMax * 0.4).toFixed(3)})`);
        grad.addColorStop(1, `rgba(${w.colorR},${w.colorG},${w.colorB},0)`);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      // Layer 3: Stroke-only lines
      for (const w of strokeWaves) {
        w.phase += w.speed;
        const cy = w.yFrac * H;

        ctx.beginPath();
        for (let x = 0; x <= W; x += 0.5) {
          const y = cy + Math.sin((x * w.freq * freqScale * Math.PI * 2) / W + w.phase) * w.amp;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(${w.strokeR},${w.strokeG},${w.strokeB},0.50)`;
        ctx.lineWidth = 10;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
      }

      animId = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener('resize', resize);
    const ro = new ResizeObserver(resize);
    ro.observe(document.body);

    draw();

    return () => {
      running = false;
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      ro.disconnect();
    };
  }, [loading]);

  useEffect(() => {
    async function loadPage() {
      try {
        const supabase = createClient();

        // Load user profile — if it fails, we already have fallback data
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('id, username, full_name, first_name, last_name, avatar_url, profession, role, created_at, referred_by')
          .eq('username', username)
          .maybeSingle();

        if (profileData) {
          setProfile(profileData as MemberProfile);

          // Load member page customization
          const { data: pageData } = await supabase
            .from('member_pages')
            .select('*')
            .eq('user_id', profileData.id)
            .maybeSingle();

          if (pageData) {
            // Use user_profiles.avatar_url as fallback for profile_photo_url
            // Never use avatar_url as cover_photo_url — treat it as empty if they match
            const rawCover = pageData.cover_photo_url || '';
            const avatarUrl = profileData.avatar_url || '';
            const safeCover = rawCover && rawCover !== avatarUrl ? rawCover : '';
            const mergedPage = {
              ...defaultPage,
              ...pageData,
              cover_photo_url: safeCover,
              profile_photo_url: pageData.profile_photo_url || avatarUrl,
            };
            setPage(mergedPage);
          } else {
            // No member_pages row — seed defaults with avatar from user_profiles
            setPage({ ...defaultPage, profile_photo_url: profileData.avatar_url || '' });
          }

          // Load growth_profile for referral_url and additional headline/bio
          const { data: growthData } = await supabase
            .from('growth_profiles')
            .select('referral_url, headline, personal_story, biography, cover_photo_url, avatar_url, whatsapp, messenger, social_links, current_rank')
            .eq('user_id', profileData.id)
            .maybeSingle();

          if (growthData) {
            setPage((prev) => ({
              ...prev,
              // Only override if member_pages fields are empty
              // Never use avatar_url as cover_photo_url
              cover_photo_url: (() => {
                if (prev.cover_photo_url) return prev.cover_photo_url;
                const gc = growthData.cover_photo_url || '';
                const ga = growthData.avatar_url || profileData.avatar_url || '';
                return gc && gc !== ga ? gc : '';
              })(),
              profile_photo_url: prev.profile_photo_url || growthData.avatar_url || profileData.avatar_url || '',
              headline: prev.headline !== defaultPage.headline ? prev.headline : (growthData.headline || prev.headline),
              about_me: prev.about_me || growthData.personal_story || '',
              biography: prev.biography || growthData.biography || '',
              whatsapp: prev.whatsapp || growthData.whatsapp || '',
              messenger: prev.messenger || growthData.messenger || '',
            }));
          }

          // Load sponsor info
          if ((profileData as any).referred_by) {
            const { data: sponsorData } = await supabase
              .from('user_profiles')
              .select('username, full_name, avatar_url')
              .eq('username', (profileData as any).referred_by)
              .maybeSingle();
            if (sponsorData) {
              setSponsor({
                username: sponsorData.username,
                full_name: sponsorData.full_name,
                avatar_url: sponsorData.avatar_url,
                rank: 'Verified Member',
                joined_year: '2025',
              });
            }
          }

          // Track visit (fire-and-forget, don't block render)
          supabase.from('page_visits').insert({
            page_owner_id: profileData.id,
            referral_source: refSource || 'direct',
            is_qr_scan: isQrScan || false,
          }).then(() => {});

          supabase.rpc('increment_page_visits', {
            page_owner: profileData.id,
            is_qr: isQrScan || false,
          }).then(() => {});
        }
        // If profileData is null, we silently use the fallback profile already set in state
      } catch {
        // Supabase error — silently use fallback data, page still renders
      } finally {
        setLoading(false);
      }
    }

    loadPage();
  }, [username, isQrScan, refSource]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSponsorRef = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('sponsorRef', username);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: `Join ${profile?.full_name} on RaketNow`, url: pageUrl });
    } else {
      handleCopyLink();
    }
  };

  const accent = page.accent_color || '#FF6B35';
  const registrationUrl = `/login?ref=${username}`;
  const joinedYear = profile?.created_at ? new Date(profile.created_at).getFullYear() : 2025;
  const displayName = profile?.full_name || username;
  const displayProfession = profile?.profession || 'RaketNow Member';
  const testimonials = page.testimonials?.length > 0 ? page.testimonials : defaultTestimonials;

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 64;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setNavOpen(false);
  };

  useEffect(() => {
    if (loading) return;
    const TOPBAR_HEIGHT = 64;

    function updateActiveSection() {
      const scrollY = window.scrollY + TOPBAR_HEIGHT + 8;
      const sectionIds = navItems.map((n) => n.id);
      let current = '';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + window.scrollY;
        if (scrollY >= top) {
          current = id;
        }
      }
      setActiveSection(current);
    }

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    return () => window.removeEventListener('scroll', updateActiveSection);
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full mx-auto animate-pulse" style={{ background: `${accent}30` }} />
          <p className="text-sm font-medium" style={{ color: '#64748B' }}>Loading page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#EEF2F7', fontFamily: 'Manrope, sans-serif' }}>
      {/* ── CANVAS WAVE BACKGROUND ── */}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* All page content sits above the wave background */}
      <div style={{ position: 'relative', zIndex: 1 }}>

      {/* ── STICKY TOPBAR NAV ── */}
      <nav className="sticky top-0 z-50 w-full" style={{ background: '#0D1B2A', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between" style={{ height: 56 }}>
          {/* Logo / brand */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="font-bold text-sm text-yellow-400 truncate max-w-[140px] hover:text-yellow-300 transition-colors"
          >
            @{username}
          </button>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center gap-1 overflow-x-auto ml-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
                style={{
                  color: activeSection === item.id ? '#FACC15' : 'rgba(255,255,255,0.55)',
                  background: 'transparent',
                  borderBottom: activeSection === item.id ? '2px solid #FACC15' : '2px solid transparent',
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* CTA + mobile hamburger */}
          <div className="flex items-center gap-2 ml-4">
            <a
              href="/login"
              className="hidden md:inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{ background: '#FACC15', color: '#0D1B2A' }}
            >
              Login
            </a>
            <button
              onClick={() => setNavOpen(!navOpen)}
              className="md:hidden flex flex-col justify-center items-center w-9 h-9 rounded-lg gap-1.5"
              style={{ background: 'rgba(255,255,255,0.08)' }}
              aria-label="Toggle menu"
            >
              <span className="block w-5 h-0.5 bg-white rounded-full transition-all" style={{ transform: navOpen ? 'rotate(45deg) translate(2px, 2px)' : 'none' }} />
              <span className="block w-5 h-0.5 bg-white rounded-full transition-all" style={{ opacity: navOpen ? 0 : 1 }} />
              <span className="block w-5 h-0.5 bg-white rounded-full transition-all" style={{ transform: navOpen ? 'rotate(-45deg) translate(2px, -2px)' : 'none' }} />
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {navOpen && (
          <div className="md:hidden px-4 pb-3 pt-1 flex flex-col gap-1" style={{ background: 'rgba(15,23,42,0.98)' }}>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="w-full text-left px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{
                  color: activeSection === item.id ? '#FACC15' : 'rgba(255,255,255,0.65)',
                  background: 'transparent',
                }}
              >
                {item.label}
              </button>
            ))}
            <a
              href="/login"
              className="w-full text-center px-4 py-2.5 rounded-xl text-sm font-semibold mt-1"
              style={{ background: '#FACC15', color: '#0D1B2A' }}
            >
              Login
            </a>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section id="hero" className="relative overflow-hidden">
        {/* Cover */}
        <div className="relative w-full overflow-hidden" style={{ height: '480px' }}>
          {page.cover_photo_url ? (
            <Image src={page.cover_photo_url} alt="Cover photo" fill sizes="100vw" className="object-cover object-top" />
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img src="/assets/images/coverphoto-1782064520616.png" alt="Cover photo" className="absolute inset-0 w-full h-full object-cover object-top" />
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(15,23,42,0.7) 100%)' }} />
        </div>

        {/* Profile card */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative mt-[-64px] md:mt-[-72px]">
            <div className="rounded-2xl p-6 md:p-8 shadow-2xl relative" style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.8)' }}>
              {/* AureGen logo — top-right corner */}
              <div className="absolute top-6 right-6 md:top-8 md:right-8" style={{ marginTop: '-25px' }}>
                <img
                  src="/assets/images/auregen-1782064364683.png"
                  alt="AureGen Logo"
                  className="w-36 h-36 md:w-48 md:h-48 object-contain"
                />
              </div>
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden shadow-lg" style={{ border: `3px solid ${accent}` }}>
                    {page.profile_photo_url || profile?.avatar_url ? (
                      <Image src={page.profile_photo_url || profile?.avatar_url || ''} alt={displayName} fill className="object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-white" style={{ background: `linear-gradient(135deg, ${accent}, #FACC15)` }}>
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-md" style={{ background: '#10B981' }}>
                    <Shield size={14} className="text-white" />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#0F172A' }}>{displayName}</h1>
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#10B981' }}>
                      <Shield size={10} /> Verified
                    </span>
                  </div>
                  <p className="text-sm font-medium mb-2" style={{ color: accent }}>{profile?.profession ? `${profile.profession} · ` : ''}@{username}</p>
                  <div className="flex flex-wrap gap-3 text-xs mb-4" style={{ color: '#64748B' }}>
                    <span className="flex items-center gap-1"><MapPin size={12} /> Philippines</span>
                    <span className="flex items-center gap-1"><Star size={12} className="text-yellow-400" /> 4.9 Rating</span>
                    <span className="flex items-center gap-1"><Users size={12} /> {page.total_visits}+ Visitors</span>
                    <span className="flex items-center gap-1"><Calendar size={12} /> Member since {joinedYear}</span>
                  </div>
                  <p className="text-base md:text-lg font-semibold leading-snug mb-4" style={{ color: '#0F172A' }}>
                    {page.headline}
                  </p>
                  <p className="text-sm mb-5" style={{ color: '#475569' }}>{page.subheadline}</p>

                  {/* Feature checklist */}
                  <div className="grid grid-cols-2 gap-1.5 mb-6 text-sm">
                    {['Services Marketplace', 'Products Marketplace', 'Professional Portfolio', 'Business Networking', 'Community Features', 'Referral Rewards'].map((f) => (
                      <div key={f} className="flex items-center gap-1.5" style={{ color: '#334155' }}>
                        <CheckCircle size={14} style={{ color: '#10B981', flexShrink: 0 }} />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTAs */}
                  <div className="flex flex-wrap gap-3">
                    <Link href={registrationUrl} onClick={handleSponsorRef} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm shadow-lg transition-transform hover:scale-105" style={{ background: `linear-gradient(135deg, ${accent}, #FACC15)` }}>
                      {page.primary_cta_text} <ArrowRight size={16} />
                    </Link>
                    <button className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105" style={{ background: '#0F172A', color: '#fff' }}>
                      <Smartphone size={16} /> {page.secondary_cta_text}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── QR CODE & SHARE ── */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-8">
        <QRCodeSection url={pageUrl} username={username} memberName={displayName} accent={accent} />
      </section>

      {/* ── AUREGEN BRAND STATEMENT ── */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-10">
        <div className="rounded-2xl px-8 py-8 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF9C3 60%, #FFFBEB 100%)', border: '1px solid rgba(250,204,21,0.45)' }}>
          {/* Decorative glow */}
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(250,204,21,0.18) 0%, transparent 70%)' }} />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.10) 0%, transparent 70%)' }} />
          <div className="relative text-center">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-4" style={{ background: 'rgba(250,204,21,0.30)', color: '#92400E', border: '1px solid rgba(250,204,21,0.5)' }}>
              AUREGEN BRAND STATEMENT
            </span>
            <p className="text-base md:text-lg font-semibold leading-relaxed" style={{ color: '#1C1917' }}>
              Auregen is the ultimate community launchpad where an evolving generation returns to the origin of economic independence to reinvent their livelihoods. By fusing a built-in social media core with an on-demand marketplace for manual services and e-commerce goods, we empower freelancers and everyday earners to generate a gold standard of sustainable income while projecting a trusted professional aura that commands market success.
            </p>
          </div>
        </div>
      </section>

      {/* ── COMPANY INTRO: Welcome to Auregen ── */}
      <section id="welcome" className="mt-10 py-14" style={{ background: '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row gap-10 items-center">
            <div className="flex-1">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-4" style={{ background: 'rgba(250,204,21,0.20)', color: '#92400E', border: '1px solid rgba(250,204,21,0.4)' }}>
                WELCOME TO AUREGEN
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight" style={{ color: '#0D1B2A' }}>
                The All-in-One Ecosystem for Personal and Economic Reinvention
              </h2>
              <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: '#475569' }}>
                In a world where traditional employment is increasingly unstable, people need more than just job boards—they need an immediate, accessible way to sustain themselves.
              </p>
              <p className="text-sm md:text-base leading-relaxed" style={{ color: '#475569' }}>
                Welcome to Auregen—the ultimate on-demand community marketplace that seamlessly fuses a powerful built-in social media network with an all-inclusive marketplace for home services, manual labor, and physical e-commerce. We are built with a single, disruptive mission: to provide the unemployed, freelancers, tradespeople, and local entrepreneurs with a zero-barrier ecosystem to reinvent their livelihoods and generate life-changing income.
              </p>
            </div>
            <div className="flex-shrink-0 flex flex-col gap-4 w-full md:w-64">
              {[
                { icon: Users, label: 'Community-First', desc: 'Built around real human connections' },
                { icon: Zap, label: 'Zero Barriers', desc: 'Start earning from day one, free' },
                { icon: Globe, label: 'Local & Regional', desc: 'Serve your neighborhood or beyond' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 rounded-xl p-4" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(250,204,21,0.18)' }}>
                    <item.icon size={18} style={{ color: '#B45309' }} />
                  </div>
                  <div>
                    <p className="text-xs font-bold" style={{ color: '#0D1B2A' }}>{item.label}</p>
                    <p className="text-xs" style={{ color: '#64748B' }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPANY INTRO: The Auregen Ecosystem ── */}
      <section className="py-14" style={{ background: 'rgba(241,245,249,0.6)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-4" style={{ background: 'rgba(13,27,42,0.08)', color: '#0D1B2A', border: '1px solid rgba(13,27,42,0.15)' }}>
              THE AUREGEN ECOSYSTEM
            </span>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#0D1B2A' }}>Social Media Meets Real-World Commerce</h2>
            <p className="text-sm md:text-base max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Auregen breaks down the walls of traditional marketplaces. By using our social media platform as the core connecting point, we transform transactions into relationships.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: Wrench,
                title: 'On-Demand Local Services',
                desc: 'Instantly book or offer any manual service—from home repairs, cleaning, and moving to plumbing, beauty services, and hands-on labor.',
                color: '#F97316',
              },
              {
                icon: ShoppingBag,
                title: 'E-Commerce Marketplace',
                desc: 'Buy and sell physical products locally or regionally with ease, turning everyday sellers into thriving storefront owners.',
                color: '#B45309',
              },
              {
                icon: Heart,
                title: 'Built-In Social Engine',
                desc: 'View video portfolios, share real-time updates, post reviews, and organically build a trusted digital presence that attracts clients.',
                color: '#0D1B2A',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl p-6 flex flex-col gap-3" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${item.color}18` }}>
                  <item.icon size={22} style={{ color: item.color }} />
                </div>
                <h3 className="font-bold text-sm" style={{ color: '#0D1B2A' }}>{item.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPANY INTRO: Designed for the People ── */}
      <section className="py-14" style={{ background: '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row gap-10 items-start">
            <div className="flex-1">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-4" style={{ background: 'rgba(249,115,22,0.12)', color: '#C2410C', border: '1px solid rgba(249,115,22,0.3)' }}>
                DESIGNED FOR THE PEOPLE
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight" style={{ color: '#0D1B2A' }}>Low Fees, High Rewards</h2>
              <p className="text-sm md:text-base leading-relaxed mb-6" style={{ color: '#475569' }}>
                We believe the people who do the work should keep the money. Auregen is explicitly designed to maximize your take-home pay through an empowering financial structure.
              </p>
              <div className="space-y-4">
                {[
                  {
                    icon: CheckCircle,
                    title: 'Free Lifetime Membership',
                    desc: 'No upfront costs, no paywalls, and no hidden barriers to entry. Anyone can sign up and start earning immediately.',
                  },
                  {
                    icon: TrendingUp,
                    title: 'Industry-Lowest Platform Fees',
                    desc: 'We keep our operational fees incredibly low so that maximum revenue stays in the pockets of the freelancers and workers who earned it.',
                  },
                  {
                    icon: Gift,
                    title: 'Network Marketing & Incentives',
                    desc: 'Earn beyond your own labor. Our built-in referral and network marketing programs reward you with continuous passive income streams for growing the Auregen community.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 items-start rounded-xl p-4" style={{ background: '#FFF7ED', border: '1px solid rgba(249,115,22,0.18)' }}>
                    <item.icon size={20} className="flex-shrink-0 mt-0.5" style={{ color: '#F97316' }} />
                    <div>
                      <p className="text-sm font-bold mb-1" style={{ color: '#0D1B2A' }}>{item.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 w-full md:w-56 rounded-2xl p-6 text-center" style={{ background: 'linear-gradient(135deg, #0D1B2A 0%, #1a2e4a 100%)', border: '1px solid rgba(250,204,21,0.25)' }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(250,204,21,0.15)' }}>
                <Award size={28} style={{ color: '#FACC15' }} />
              </div>
              <p className="text-xs font-bold mb-1" style={{ color: '#FACC15' }}>EARN MORE</p>
              <p className="text-2xl font-bold mb-2" style={{ color: '#FFFFFF' }}>Keep More</p>
              <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>Industry-lowest fees mean your hard-earned money stays where it belongs — with you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPANY INTRO: Why "Auregen"? ── */}
      <section className="py-14" style={{ background: 'rgba(248,250,252,0.6)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-4" style={{ background: 'rgba(250,204,21,0.20)', color: '#92400E', border: '1px solid rgba(250,204,21,0.4)' }}>
              WHY "AUREGEN"?
            </span>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#0D1B2A' }}>The Origin of Reinvention</h2>
            <p className="text-sm md:text-base max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Every syllable of our name carries meaning. Auregen is more than a brand — it is a philosophy.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                label: 'AU · Aurum (Gold)',
                title: 'The Golden Standard',
                desc: 'We bring premium dignity to manual labor and local commerce, empowering everyday workers to deliver a high-quality standard that commands respect and fair pricing.',
                accent: '#B45309',
                bg: '#FFFBEB',
                border: 'rgba(250,204,21,0.35)',
              },
              {
                label: 'AURA · Magnetic Presence',
                title: 'The Magnetic Aura',
                desc: 'Through our social media core, you build a trusted, magnetic reputation. Your profile becomes your digital badge of honor.',
                accent: '#7C3AED',
                bg: '#F5F3FF',
                border: 'rgba(124,58,237,0.2)',
              },
              {
                label: 'RE · Reinvention',
                title: 'The Starting Line',
                desc: 'We are the starting line for your new life. If you are unemployed, underemployed, or looking to scale a trade, Auregen is the canvas where you can completely recreate your career.',
                accent: '#0369A1',
                bg: '#F0F9FF',
                border: 'rgba(3,105,161,0.2)',
              },
              {
                label: 'GEN · Generation',
                title: 'Continuous Generation',
                desc: 'Economic survival requires constant adaptation. Auregen acts as a continuous engine, allowing you to instantly switch between selling goods, booking services, or earning from your network.',
                accent: '#059669',
                bg: '#F0FDF4',
                border: 'rgba(5,150,105,0.2)',
              },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl p-6" style={{ background: item.bg, border: `1px solid ${item.border}` }}>
                <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-full mb-3" style={{ background: `${item.accent}18`, color: item.accent }}>
                  {item.label}
                </span>
                <h3 className="font-bold text-base mb-2" style={{ color: '#0D1B2A' }}>{item.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPANY INTRO: Reclaim Your Financial Freedom ── */}
      <section className="py-14" style={{ background: 'linear-gradient(135deg, #0D1B2A 0%, #1a2e4a 60%, #0D1B2A 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-5" style={{ background: 'rgba(250,204,21,0.15)', color: '#FACC15', border: '1px solid rgba(250,204,21,0.3)' }}>
            RECLAIM YOUR FINANCIAL FREEDOM
          </span>
          <h2 className="text-2xl md:text-3xl font-bold mb-5 leading-tight" style={{ color: '#FFFFFF' }}>
            You don't have to wait for an economy that works for you — you can build your own.
          </h2>
          <p className="text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Whether you are selling handmade goods, booking out your landscaping services, or driving community growth through our incentive network, Auregen gives you the platform to rebuild, refresh, and reclaim your financial future.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {[
              { icon: Briefcase, label: 'Sell Services', desc: 'Offer any skill or trade on demand' },
              { icon: ShoppingBag, label: 'Sell Products', desc: 'Launch your own digital storefront' },
              { icon: Users, label: 'Grow Your Network', desc: 'Earn passive income through referrals' },
            ].map((item) => (
              <div key={item.label} className="rounded-xl p-5 text-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(250,204,21,0.15)' }}>
                  <item.icon size={20} style={{ color: '#FACC15' }} />
                </div>
                <p className="text-sm font-bold mb-1" style={{ color: '#FFFFFF' }}>{item.label}</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-base md:text-lg font-semibold mb-1" style={{ color: '#FACC15' }}>Auregen.</p>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>Reinvent your livelihood. Connect through community. Begin your next chapter here.</p>
        </div>
      </section>

      {/* ── WHY JOIN ── */}
      <section id="features" className="py-12" style={{ background: 'rgba(255,255,255,0.72)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: `${accent}15`, color: accent }}>PLATFORM FEATURES</span>
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#0F172A' }}>Everything You Need To Build Your Digital Business</h2>
          <p style={{ color: '#64748B' }}>One platform. Unlimited opportunities.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: ShoppingBag, title: 'Services Marketplace', desc: 'Book or offer professional services across dozens of categories.' },
            { icon: Package, title: 'Products Marketplace', desc: 'Buy and sell products online with your own digital storefront.' },
            { icon: Briefcase, title: 'Professional Portfolio', desc: 'Showcase your skills, experience, and work to attract clients.' },
            { icon: Users, title: 'Business Networking', desc: 'Connect with professionals, entrepreneurs, and potential partners.' },
            { icon: Heart, title: 'Community', desc: 'Share updates, build relationships, and grow together.' },
            { icon: Gift, title: 'Referral Rewards', desc: 'Invite others and earn rewards through marketplace activity.' },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl p-5 transition-all hover:shadow-md" style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${accent}15` }}>
                <f.icon size={20} style={{ color: accent }} />
              </div>
              <h3 className="font-bold text-sm mb-1.5" style={{ color: '#0F172A' }}>{f.title}</h3>
              <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>{f.desc}</p>
            </div>
          ))}
        </div>
        {/* ── PHONE MOCKUP ── */}
        <div className="flex justify-center mt-14">
          <div className="relative" style={{ width: 280, height: 580 }}>
            {/* iPhone 17 Pro Max outer shell — titanium finish */}
            <div className="absolute inset-0 rounded-[52px] shadow-2xl" style={{ background: 'linear-gradient(145deg, #4a4a4a 0%, #2a2a2a 40%, #1a1a1a 60%, #3a3a3a 100%)', border: '1px solid #555' }} />
            {/* Titanium frame highlight */}
            <div className="absolute inset-0 rounded-[52px]" style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.15) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)', pointerEvents: 'none' }} />
            {/* Volume buttons (left side) */}
            <div className="absolute rounded-r-sm" style={{ left: -3, top: 120, width: 3, height: 28, background: 'linear-gradient(to right, #555, #888)' }} />
            <div className="absolute rounded-r-sm" style={{ left: -3, top: 160, width: 3, height: 52, background: 'linear-gradient(to right, #555, #888)' }} />
            <div className="absolute rounded-r-sm" style={{ left: -3, top: 222, width: 3, height: 52, background: 'linear-gradient(to right, #555, #888)' }} />
            {/* Power button (right side) */}
            <div className="absolute rounded-l-sm" style={{ right: -3, top: 160, width: 3, height: 68, background: 'linear-gradient(to left, #555, #888)' }} />
            {/* Screen area */}
            <div className="absolute overflow-hidden rounded-[44px]" style={{ top: 8, left: 6, right: 6, bottom: 8 }}>
              {/* Status bar — sits at top, below Dynamic Island space */}
              <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5" style={{ height: 44, paddingTop: 14, background: 'rgba(0,0,0,0.85)' }}>
                {/* Time */}
                <span className="text-white font-semibold" style={{ fontSize: 12, letterSpacing: 0.2 }}>9:41</span>
                {/* Right icons: signal, wifi, battery */}
                <div className="flex items-center gap-1.5">
                  {/* Signal bars */}
                  <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0" y="8" width="3" height="4" rx="0.5" fill="white"/>
                    <rect x="4.5" y="5.5" width="3" height="6.5" rx="0.5" fill="white"/>
                    <rect x="9" y="3" width="3" height="9" rx="0.5" fill="white"/>
                    <rect x="13.5" y="0" width="2.5" height="12" rx="0.5" fill="white" opacity="0.35"/>
                  </svg>
                  {/* WiFi */}
                  <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 9.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z" fill="white"/>
                    <path d="M4.5 7.2C5.6 6.1 6.7 5.5 8 5.5s2.4.6 3.5 1.7" stroke="white" strokeWidth="1.4" strokeLinecap="round" fill="none"/>
                    <path d="M1.5 4.2C3.2 2.5 5.5 1.5 8 1.5s4.8 1 6.5 2.7" stroke="white" strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.5"/>
                  </svg>
                  {/* Battery */}
                  <svg width="25" height="12" viewBox="0 0 25 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.5" y="0.5" width="21" height="11" rx="2.5" stroke="white" strokeWidth="1"/>
                    <rect x="2" y="2" width="16" height="8" rx="1.5" fill="white"/>
                    <path d="M22.5 4v4a2 2 0 0 0 0-4z" fill="white" opacity="0.4"/>
                  </svg>
                </div>
              </div>
              {/* Screenshot starts immediately below the status bar */}
              <div className="absolute left-0 right-0 bottom-0 overflow-y-auto" style={{ top: 44 }}>
                <img
                  src="/assets/images/RaketApp-1781998370797.png"
                  alt="RaketApp mobile application screenshot"
                  className="w-full"
                  style={{ display: 'block' }}
                />
              </div>
            </div>
            {/* Dynamic Island */}
            <div className="absolute left-1/2 -translate-x-1/2 rounded-full" style={{ top: 18, width: 110, height: 34, background: '#000', zIndex: 10, boxShadow: '0 0 0 1px rgba(255,255,255,0.08)' }} />
            {/* Camera pill inside Dynamic Island */}
            <div className="absolute left-1/2 -translate-x-1/2 rounded-full" style={{ top: 26, width: 10, height: 10, background: '#1a1a1a', zIndex: 11, marginLeft: 28, boxShadow: 'inset 0 0 4px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.1)' }} />
            {/* Home indicator */}
            <div className="absolute left-1/2 -translate-x-1/2 rounded-full" style={{ bottom: 16, width: 100, height: 5, background: 'rgba(255,255,255,0.4)', zIndex: 10 }} />
            {/* Rear camera bump hint (subtle reflection on bottom edge) */}
            <div className="absolute inset-x-0 bottom-0 rounded-b-[52px]" style={{ height: 60, background: 'linear-gradient(to top, rgba(255,255,255,0.04), transparent)', pointerEvents: 'none' }} />
          </div>
        </div>
        </div>
      </section>

      {/* ── SERVICES MARKETPLACE ── */}
      <section id="services" className="mt-16 py-16" style={{ background: 'transparent' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row gap-10 items-center">
            <div className="flex-1">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: `${accent}15`, color: accent }}>SERVICES</span>
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#0F172A' }}>Services Marketplace</h2>
              <p className="mb-4" style={{ color: '#475569' }}>Find trusted service providers or become one yourself. Booking is free. Creating a provider account is free.</p>
              <div className="grid grid-cols-4 gap-2 mb-6">
                {serviceCategories.map((s) => (
                  <div key={s.label} className="flex flex-col items-center gap-1 p-2 rounded-xl text-center" style={{ background: '#F8FAFC' }}>
                    <s.icon size={18} style={{ color: accent }} />
                    <span className="text-xs font-medium" style={{ color: '#475569' }}>{s.label}</span>
                  </div>
                ))}
              </div>
              <Link href={registrationUrl} onClick={handleSponsorRef} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{ background: accent }}>
                Start Offering Services <ArrowRight size={14} />
              </Link>
            </div>
            <div className="flex-shrink-0 w-full md:w-64">
              <div className="rounded-2xl p-5 shadow-xl" style={{ background: 'linear-gradient(135deg, #0F172A, #1E293B)', color: '#fff' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: accent }}>
                    <Wrench size={16} className="text-white" />
                  </div>
                  <span className="font-bold text-sm">Service Booking</span>
                </div>
                {['Home Repair', 'Cleaning', 'Design', 'Programming'].map((s) => (
                  <div key={s} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                    <span className="text-xs text-white/70">{s}</span>
                    <span className="text-xs font-bold" style={{ color: '#10B981' }}>Available</span>
                  </div>
                ))}
                <div className="mt-4 text-center">
                  <span className="text-xs text-white/50">Platform earns only from completed transactions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRODUCTS MARKETPLACE ── */}
      <section id="products" className="mt-0 py-16" style={{ background: 'rgba(255,255,255,0.72)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row-reverse gap-10 items-center">
            <div className="flex-1">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: `${accent}15`, color: accent }}>MARKETPLACE</span>
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#0F172A' }}>Products Marketplace</h2>
              <p className="mb-4" style={{ color: '#475569' }}>Open your own online store in minutes. No monthly seller fees. List unlimited products. Reach thousands of customers.</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {productCategories.map((c) => (
                  <span key={c} className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: '#fff', color: '#475569', border: '1px solid #E2E8F0' }}>{c}</span>
                ))}
              </div>
              <Link href={registrationUrl} onClick={handleSponsorRef} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{ background: accent }}>
                Open Your Store <ArrowRight size={14} />
              </Link>
            </div>
            <div className="flex-shrink-0 w-full md:w-64">
              <div className="rounded-2xl overflow-hidden shadow-xl" style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
                <div className="p-4" style={{ background: `linear-gradient(135deg, ${accent}, #FACC15)` }}>
                  <p className="text-white font-bold text-sm">🛍️ RaketMall</p>
                  <p className="text-white/80 text-xs">Thousands of products</p>
                </div>
                {['Electronics', 'Fashion', 'Beauty', 'Food'].map((c) => (
                  <div key={c} className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 last:border-0">
                    <span className="text-xs font-medium" style={{ color: '#334155' }}>{c}</span>
                    <span className="text-xs" style={{ color: '#94A3B8' }}>Shop →</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROFESSIONAL PROFILE ── */}
      <section id="profile" className="py-16" style={{ background: 'transparent' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: `${accent}15`, color: accent }}>PROFILE</span>
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#0F172A' }}>Your Digital Business Card</h2>
          <p className="mb-10 max-w-xl mx-auto" style={{ color: '#64748B' }}>Showcase your biography, experience, portfolio, certifications, projects, reviews, skills, and more. Allow visitors to connect directly with you.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['Biography', 'Experience', 'Portfolio', 'Certifications', 'Projects', 'Reviews', 'Skills', 'Gallery', 'Videos', 'Social Links', 'Contact Buttons', 'Custom URL'].map((item) => (
              <div key={item} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium" style={{ background: '#F8FAFC', color: '#334155', border: '1px solid #E2E8F0' }}>
                <CheckCircle size={12} style={{ color: '#10B981', flexShrink: 0 }} />
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMMUNITY ── */}
      <section id="community" className="py-16" style={{ background: 'rgba(255,255,255,0.72)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: `${accent}15`, color: accent }}>COMMUNITY</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#0F172A' }}>Connect With A Community</h2>
            <p style={{ color: '#64748B' }}>Build business relationships naturally through shared content and engagement.</p>
          </div>
          <div className="space-y-3">
            {[
              { user: 'Maria S.', badge: '🏆', text: 'Just hit ₱100,000 in monthly sales! Thank you RaketNow community for the support! 🎉', time: '2h ago', likes: 142 },
              { user: 'Juan D.', badge: '💎', text: 'Looking for reliable home repair providers in Metro Manila. Drop your profiles below! 👇', time: '4h ago', likes: 87 },
              { user: 'Ana R.', badge: '⭐', text: 'Pro tip: Complete your profile 100% to get 3x more visibility in the marketplace. It works!', time: '6h ago', likes: 203 },
            ].map((post, i) => (
              <div key={i} className="rounded-2xl p-4" style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: accent }}>{post.user.charAt(0)}</div>
                  <div>
                    <p className="font-bold text-sm" style={{ color: '#0F172A' }}>{post.user} <span>{post.badge}</span></p>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>{post.time}</p>
                  </div>
                </div>
                <p className="text-sm mb-3" style={{ color: '#334155' }}>{post.text}</p>
                <div className="flex gap-4 text-xs" style={{ color: '#94A3B8' }}>
                  <span>❤️ {post.likes}</span><span>💬 Comment</span><span>↗️ Share</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GETTING STARTED ── */}
      <section className="py-16" style={{ background: 'transparent' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: `${accent}15`, color: accent }}>GET STARTED</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#0F172A' }}>Getting Started Is Easy</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { step: '01', title: 'Create Your FREE Account', desc: 'No membership fee required. Immediately access the Marketplace, Professional Profile, Community, and Networking features.', icon: '🚀', highlight: false },
              { step: '02', title: 'Download the Mobile App', desc: 'Manage your marketplace, bookings, messages, referrals, customers, and business from anywhere.', icon: '📱', highlight: false },
              { step: '03', title: 'Complete Your Profile', desc: 'Increase trust and improve visibility. A complete profile gets 3x more engagement.', icon: '✅', highlight: false },
              { step: '04', title: 'Activate Referral Rewards (Optional)', desc: 'Members who wish to participate in the optional referral rewards program may activate this feature. Unlocks: Referral Dashboard, Genealogy, Rank Advancement, Team Coaching, and Business Analytics.', icon: '🎯', highlight: true },
            ].map((s) => (
              <div key={s.step} className="rounded-2xl p-5 relative overflow-hidden" style={{ background: '#ffffff', border: `1px solid ${s.highlight ? accent + '30' : '#E2E8F0'}` }}>
                {s.highlight && <span className="absolute top-3 right-3 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${accent}20`, color: accent }}>Optional</span>}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: s.highlight ? `${accent}20` : '#fff', border: `1px solid ${s.highlight ? accent + '30' : '#E2E8F0'}` }}>{s.icon}</div>
                  <div>
                    <div className="text-xs font-bold mb-1" style={{ color: accent }}>STEP {s.step}</div>
                    <h3 className="font-bold text-sm mb-2" style={{ color: '#0F172A' }}>{s.title}</h3>
                    <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW REWARDS WORK ── */}
      <section id="rewards" className="py-16" style={{ background: 'rgba(255,255,255,0.72)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: `${accent}15`, color: accent }}>REWARDS</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#0F172A' }}>How Rewards Work</h2>
            <p className="max-w-xl mx-auto" style={{ color: '#64748B' }}>Rewards are based on real marketplace activity — not just recruitment. The more your network shops and books, the more everyone earns.</p>
          </div>
          <div className="relative">
            <div className="absolute left-6 top-6 bottom-6 w-0.5" style={{ background: `${accent}30` }} />
            <div className="space-y-4">
              {rewardFlow.map((r, i) => (
                <div key={i} className="flex items-center gap-4 pl-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 shadow-md" style={{ background: i === rewardFlow.length - 1 ? accent : '#fff', border: `2px solid ${accent}` }}>
                    <r.icon size={16} style={{ color: i === rewardFlow.length - 1 ? '#fff' : accent }} />
                  </div>
                  <div className="flex-1 rounded-xl px-4 py-3" style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
                    <p className="text-sm font-medium" style={{ color: '#334155' }}>{r.step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-16" style={{ background: 'transparent' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: `${accent}15`, color: accent }}>FAQ</span>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#0F172A' }}>Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <div key={i} className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <span className="font-semibold text-sm pr-4" style={{ color: '#0F172A' }}>{item.q}</span>
                  {openFaq === i ? <ChevronUp size={16} style={{ color: accent, flexShrink: 0 }} /> : <ChevronDown size={16} style={{ color: '#94A3B8', flexShrink: 0 }} />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-sm leading-relaxed" style={{ color: '#64748B' }}>{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MARKETING PLAN ── */}
      <div id="marketing-plan">
        <MarketingPlanSection accent={accent} />
      </div>

      {/* ── FINAL CTA ── */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #0D1B2A 0%, #0F2744 40%, #0F172A 100%)' }}>
        {/* Aggressive moving glowing lights */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
          <div style={{
            position: 'absolute',
            top: '-80px',
            right: '-80px',
            width: '520px',
            height: '520px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #00b4d8 0%, transparent 65%)',
            animation: 'prefooterGlow1 8s ease-in-out infinite, prefooterFlicker1 3s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-60px',
            left: '-80px',
            width: '420px',
            height: '420px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #ff6b35 0%, transparent 65%)',
            animation: 'prefooterGlow2 10s ease-in-out infinite, prefooterFlicker2 4s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '480px',
            height: '480px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #FACC15 0%, transparent 70%)',
            animation: 'prefooterGlow3 12s ease-in-out infinite, prefooterFlicker3 5s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute',
            top: '20%',
            left: '15%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #a855f7 0%, transparent 65%)',
            animation: 'prefooterGlow4 7s ease-in-out infinite, prefooterFlicker4 3.5s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: '360px',
            height: '360px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #10b981 0%, transparent 65%)',
            animation: 'prefooterGlow5 9s ease-in-out infinite, prefooterFlicker5 4.5s ease-in-out infinite',
          }} />
        </div>
        <style>{`
          @keyframes prefooterGlow1 {
            0%   { transform: translate(0px, 0px); }
            10%  { transform: translate(-60px, 40px); }
            22%  { transform: translate(30px, 90px); }
            35%  { transform: translate(100px, 20px); }
            48%  { transform: translate(50px, -70px); }
            60%  { transform: translate(-40px, -100px); }
            72%  { transform: translate(-110px, -30px); }
            85%  { transform: translate(-20px, 60px); }
            100% { transform: translate(0px, 0px); }
          }
          @keyframes prefooterFlicker1 {
            0%,100% { opacity: 0.45; }
            15%     { opacity: 0.58; }
            30%     { opacity: 0.32; }
            50%     { opacity: 0.52; }
            65%     { opacity: 0.28; }
            80%     { opacity: 0.50; }
          }
          @keyframes prefooterGlow2 {
            0%   { transform: translate(0px, 0px); }
            12%  { transform: translate(80px, -50px); }
            25%  { transform: translate(120px, 40px); }
            38%  { transform: translate(40px, 110px); }
            52%  { transform: translate(-60px, 80px); }
            65%  { transform: translate(-110px, -20px); }
            78%  { transform: translate(-40px, -90px); }
            90%  { transform: translate(50px, -60px); }
            100% { transform: translate(0px, 0px); }
          }
          @keyframes prefooterFlicker2 {
            0%,100% { opacity: 0.40; }
            20%     { opacity: 0.25; }
            38%     { opacity: 0.55; }
            55%     { opacity: 0.22; }
            70%     { opacity: 0.48; }
            88%     { opacity: 0.30; }
          }
          @keyframes prefooterGlow3 {
            0%   { transform: translate(-50%, -50%); }
            8%   { transform: translate(calc(-50% + 70px), calc(-50% - 50px)); }
            20%  { transform: translate(calc(-50% + 110px), calc(-50% + 30px)); }
            33%  { transform: translate(calc(-50% + 40px), calc(-50% + 100px)); }
            46%  { transform: translate(calc(-50% - 50px), calc(-50% + 70px)); }
            58%  { transform: translate(calc(-50% - 100px), calc(-50% - 20px)); }
            70%  { transform: translate(calc(-50% - 60px), calc(-50% - 90px)); }
            82%  { transform: translate(calc(-50% + 20px), calc(-50% - 80px)); }
            92%  { transform: translate(calc(-50% + 80px), calc(-50% - 40px)); }
            100% { transform: translate(-50%, -50%); }
          }
          @keyframes prefooterFlicker3 {
            0%,100% { opacity: 0.28; }
            18%     { opacity: 0.40; }
            35%     { opacity: 0.18; }
            52%     { opacity: 0.35; }
            68%     { opacity: 0.15; }
            84%     { opacity: 0.32; }
          }
          @keyframes prefooterGlow4 {
            0%   { transform: translate(0px, 0px); }
            15%  { transform: translate(90px, -60px); }
            30%  { transform: translate(-50px, 80px); }
            50%  { transform: translate(70px, 100px); }
            65%  { transform: translate(-80px, 50px); }
            80%  { transform: translate(40px, -80px); }
            100% { transform: translate(0px, 0px); }
          }
          @keyframes prefooterFlicker4 {
            0%,100% { opacity: 0.35; }
            25%     { opacity: 0.50; }
            50%     { opacity: 0.22; }
            75%     { opacity: 0.45; }
          }
          @keyframes prefooterGlow5 {
            0%   { transform: translate(0px, 0px); }
            20%  { transform: translate(-70px, -80px); }
            40%  { transform: translate(60px, -100px); }
            60%  { transform: translate(100px, 50px); }
            80%  { transform: translate(-40px, 70px); }
            100% { transform: translate(0px, 0px); }
          }
          @keyframes prefooterFlicker5 {
            0%,100% { opacity: 0.38; }
            20%     { opacity: 0.22; }
            45%     { opacity: 0.50; }
            70%     { opacity: 0.18; }
            90%     { opacity: 0.42; }
          }
        `}</style>

        {/* Subtle radial glow bottom-left */}
        <div aria-hidden="true" style={{ position: 'absolute', bottom: '-80px', left: '-60px', width: '340px', height: '340px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(22,101,52,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
        {/* Subtle radial glow top-right */}
        <div aria-hidden="true" style={{ position: 'absolute', top: '-60px', right: '-40px', width: '260px', height: '260px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(14,116,144,0.14) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative">
          <img src="/assets/images/raketnow-1781893444669.png" alt="RaketNow Logo" className="mx-auto mb-3 h-[115px] w-auto object-contain" />
          <p className="text-white/80 font-bold text-xl tracking-widest uppercase mb-6" style={{ letterSpacing: '0.18em', background: 'linear-gradient(90deg, #FACC15 0%, #F97316 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>RaketNow</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ background: 'linear-gradient(90deg, #FACC15 0%, #F97316 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Your Business Starts Today.</h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto text-lg">
            Join under <strong className="text-white">{displayName}</strong> and start building your professional presence, selling products, offering services, and growing your opportunities inside one complete digital ecosystem.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link href={registrationUrl} onClick={handleSponsorRef} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-base shadow-2xl transition-transform hover:scale-105" style={{ background: `linear-gradient(135deg, ${accent}, #FACC15)`, color: '#0F172A' }}>
              Create My Free Account <ArrowRight size={18} />
            </Link>
            <a href="https://drive.google.com/file/d/1C8ga204y-QZICNHJziIE-UZbPAAzd0Gi/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base border-2 border-white/30 text-white transition-all hover:bg-white/10">
              <Smartphone size={18} /> Download Mobile App
            </a>
          </div>

          {/* Sponsor card in final CTA */}
          <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" style={{ border: `2px solid ${accent}` }}>
              {page.profile_photo_url || profile?.avatar_url ? (
                <Image src={page.profile_photo_url || profile?.avatar_url || ''} alt={displayName} width={48} height={48} className="object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-base font-bold text-white" style={{ background: accent }}>
                  {displayName.charAt(0)}
                </div>
              )}
            </div>
            <div className="text-left">
              <p className="text-white/60 text-xs mb-0.5">Your sponsor</p>
              <p className="text-white font-bold">{displayName}</p>
              <p className="text-white/50 text-xs">@{username} · Verified Member</p>
            </div>
            <div className="flex gap-2 ml-4">
              {page.whatsapp && (
                <a href={`https://wa.me/${page.whatsapp}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#25D366' }}>
                  <MessageCircle size={16} className="text-white" />
                </a>
              )}
              {page.call_number && (
                <a href={`tel:${page.call_number}`} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: accent }}>
                  <Phone size={16} className="text-white" />
                </a>
              )}
              {page.email_contact && (
                <a href={`mailto:${page.email_contact}`} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#6366F1' }}>
                  <Mail size={16} className="text-white" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* AURE logo at the bottom of the pre-footer section */}
        <div className="mt-16 flex justify-center items-center">
          <Image
            src="/assets/images/auregen-white-1782064520503.png"
            alt="AURE Growing Futures logo"
            width={540}
            height={180}
            className="object-contain"
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center" style={{ background: '#0F172A' }}>
        <p className="text-xs" style={{ color: '#475569' }}>
          © {new Date().getFullYear()} RaketNow · This is a personal page of <strong className="text-white/60">@{username}</strong>
        </p>
        <p className="text-xs mt-2" style={{ color: 'rgba(128,128,128,0.8)' }}>Connecting subscribers with trusted service providers</p>
        <p className="text-xs mt-1" style={{ color: 'rgba(128,128,128,0.8)' }}>
          Made with <span style={{ color: '#FACC15' }}>♥</span> by{' '}
          <span className="font-bold" style={{ color: '#FACC15' }}>DB82</span>
        </p>
      </footer>
      </div>
    </div>
  );
}
